import * as React from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Loader2, type LucideIcon } from "lucide-react"
import { useNavigate } from 'react-router-dom';

export interface PageAction {
  label: string
  onClick?: () => void
  loading?: boolean
  disabled?: boolean
  icon?: LucideIcon
  type?: "submit" | "button"
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
}

interface PageActionsProps {
  primaryAction?: PageAction
  secondaryActions?: PageAction[]
  cancelAction?: {
    label?: string
    onClick?: () => void
    href?: string
    disabled?: boolean
  }
  className?: string
  children?: React.ReactNode
}

export function PageActions({
  primaryAction,
  secondaryActions = [],
  cancelAction = { label: "Cancel" },
  className,
  children
}: PageActionsProps) {
  const navigate = useNavigate()

  const handleCancel = () => {
    if (cancelAction.onClick) {
      cancelAction.onClick()
    } else if (cancelAction.href) {
      navigate(cancelAction.href)
    } else {
      navigate(-1)
    }
  }

  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 py-1.5 px-4 bg-background/95 backdrop-blur border-t flex items-center justify-end gap-4 z-40 md:pl-72 shadow-[0_-4px_6px_-1px_rgb(0_0_0_/_0.05)]",
      className
    )}>
      {children}

      {/* Secondary Actions */}
      {secondaryActions.map((action, index) => (
        <Button
          key={index}
          type={action.type || "button"}
          variant={action.variant || "outline"}
          onClick={action.onClick}
          disabled={action.disabled || action.loading}
        >
          {action.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {action.icon && !action.loading && <action.icon className="mr-2 h-4 w-4" />}
          {action.label}
        </Button>
      ))}

      {/* Cancel Action */}
      {(cancelAction.onClick || cancelAction.href || !cancelAction.onClick) && (
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={cancelAction.disabled}
        >
          {cancelAction.label || "Cancel"}
        </Button>
      )}

      {/* Primary Action */}
      {primaryAction && (
        <Button
          type={primaryAction.type || "submit"}
          variant={primaryAction.variant || "default"}
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled || primaryAction.loading}
          className="min-w-[140px]"
        >
          {primaryAction.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {primaryAction.icon && !primaryAction.loading && <primaryAction.icon className="mr-2 h-4 w-4" />}
          {primaryAction.label}
        </Button>
      )}
    </div>
  )
}
